import React from 'react';
import { Container, Card, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Container className="mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Privacy Policy</h2>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </div>

      <Card>
        <Card.Body>
          <div className="mb-4">
            <p className="text-muted">
              <strong>Last updated:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>

          <section className="mb-4">
            <h4>1. Introduction</h4>
            <p>
              EWPulseSync ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
          </section>

          <section className="mb-4">
            <h4>2. Information We Collect</h4>
            
            <h5>2.1 Personal Information</h5>
            <p>We may collect the following personal information:</p>
            <ul>
              <li>Email address</li>
              <li>Display name</li>
              <li>Account credentials</li>
              <li>Billing information (processed securely through Stripe)</li>
            </ul>

            <h5>2.2 Usage Information</h5>
            <p>We automatically collect certain information about your use of the Service:</p>
            <ul>
              <li>Device information (browser type, operating system)</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Log files and analytics data</li>
            </ul>

            <h5>2.3 Cookies and Tracking</h5>
            <p>
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and provide personalized content.
            </p>
          </section>

          <section className="mb-4">
            <h4>3. How We Use Your Information</h4>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>Provide and maintain the Service</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send important service notifications</li>
              <li>Improve and personalize your experience</li>
              <li>Analyze usage patterns and optimize performance</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-4">
            <h4>4. Information Sharing and Disclosure</h4>
            <p>We do not sell, trade, or rent your personal information. We may share your information in the following circumstances:</p>
            
            <h5>4.1 Service Providers</h5>
            <p>We may share information with trusted third-party service providers:</p>
            <ul>
              <li><strong>Stripe:</strong> For payment processing and billing</li>
              <li><strong>Firebase/Google:</strong> For authentication and data storage</li>
              <li><strong>Analytics providers:</strong> For usage analysis and improvement</li>
            </ul>

            <h5>4.2 Legal Requirements</h5>
            <p>We may disclose information when required by law or to:</p>
            <ul>
              <li>Comply with legal processes</li>
              <li>Protect our rights and property</li>
              <li>Ensure user safety</li>
              <li>Prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="mb-4">
            <h4>5. Data Security</h4>
            <p>We implement appropriate security measures to protect your information:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Limited access to personal information</li>
            </ul>
            <p>
              However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-4">
            <h4>6. Data Retention</h4>
            <p>
              We retain your personal information for as long as necessary to provide the Service and fulfill the purposes outlined in this Privacy Policy. We may retain certain information for longer periods to comply with legal obligations or resolve disputes.
            </p>
          </section>

          <section className="mb-4">
            <h4>7. Your Rights and Choices</h4>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided below.
            </p>
          </section>

          <section className="mb-4">
            <h4>8. Third-Party Services</h4>
            <p>
              Our Service integrates with third-party services. These services have their own privacy policies, and we encourage you to review them:
            </p>
            <ul>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a></li>
              <li><strong>Firebase/Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
            </ul>
          </section>

          <section className="mb-4">
            <h4>9. International Data Transfers</h4>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information during such transfers.
            </p>
          </section>

          <section className="mb-4">
            <h4>10. Children's Privacy</h4>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-4">
            <h4>11. Changes to This Privacy Policy</h4>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-4">
            <h4>12. Contact Us</h4>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <ul>
              <li>Email: privacy@ewpulsesync.com</li>
              <li>Website: https://pulsesync.bcntr.com</li>
              <li>Support: support@ewpulsesync.com</li>
            </ul>
          </section>

          <div className="mt-5 pt-4 border-top">
            <p className="text-muted small">
              By using EWPulseSync, you acknowledge that you have read and understood this Privacy Policy and agree to the collection, use, and disclosure of your information as described herein.
            </p>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PrivacyPolicy;
